import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { isDefined } from '../../../../../utils/variables';
import { GeodataSetupContext } from '../../../../state/geodata-setup-state';
import { ProjectContext } from '../../../../state/project-state/project-state';
import { Icon } from '../../../icon/icon';
import { IconPanel } from '../../../icon-panel/icon-panel';
import { ProgressBar } from '../../../progress-bar/progress-bar';
import { Stack } from '../../../stack/stack';
import { useControlImagesStep } from '../use-control-images-step';
import { useCreateGeodataStep } from '../use-create-geodata-step';
import { useStartCloudProcessStep } from '../use-start-cloud-process-step';
import { useUploadBoundaryPolygonsStep } from '../use-upload-boundary-polygons-step';
import { useUploadImagesStep } from '../use-upload-images-step';

export const GeodataFinish = () => {
  const { t } = useTranslation('cloudProcessing');
  const context = useContext(GeodataSetupContext);
  const projectContext = useContext(ProjectContext);
  const projectId = projectContext?.project.id;
  const navigate = useNavigate();

  const {
    errorList: createGeodataErrorList,
    geodataId,
    createStepStatus,
  } = useCreateGeodataStep(projectId, context);
  const {
    boundaryUploadQueue,
    uploadStepStatus: boundaryStepStatus,
    progress: boundaryProgress,
    errorList: boundaryUploadErrorList,
  } = useUploadBoundaryPolygonsStep(context, geodataId, createStepStatus);
  const {
    imageUploadQueue,
    uploadStepStatus: imageStepStatus,
    progress: imageProgress,
    errorList: imageUploadErrorList,
  } = useUploadImagesStep(context, geodataId, boundaryStepStatus);
  const {
    uploadStepStatus: controlImagesStepStatus,
    errorList: controlImagesErrorList,
    enabledCount: controlImagesEnabledCount,
  } = useControlImagesStep(geodataId, imageUploadQueue, imageStepStatus);
  const { uploadStepStatus: startProcessStepStatus, errorList: startProcessErrorList } =
    useStartCloudProcessStep(context, geodataId, projectId, controlImagesStepStatus);

  // Redirect to GCP page when all steps are complete and useInteractiveSetup is true
  useEffect(() => {
    if (!isDefined(context)) {
      return;
    }

    if (
      boundaryStepStatus === 'success' &&
      imageStepStatus === 'success' &&
      context.useInteractiveSetup &&
      geodataId &&
      projectId
    ) {
      if (navigate) {
        void navigate(`/projects/${projectId}/geodata/${geodataId}/viewer`);
      }
    }
  }, [boundaryStepStatus, imageStepStatus, context, geodataId, projectId, navigate]);

  useEffect(() => {
    if (!isDefined(context)) {
      return;
    }
    const allTerminal =
      startProcessStepStatus === 'success' ||
      createStepStatus === 'error' ||
      boundaryStepStatus === 'error' ||
      imageStepStatus === 'error' ||
      controlImagesStepStatus === 'error' ||
      startProcessStepStatus === 'error';
    if (allTerminal) {
      context.setIsFinishComplete(true);
    }
  }, [
    context,
    startProcessStepStatus,
    createStepStatus,
    boundaryStepStatus,
    imageStepStatus,
    controlImagesStepStatus,
  ]);

  if (!isDefined(context)) {
    return null;
  }

  if (startProcessStepStatus === 'success' && !context.useInteractiveSetup) {
    return (
      <Component>
        <h4>{t('geodataSetup.finish.title', { ns: 'cloudProcessing' })}</h4>
        <CenteredContainer>
          <IconPanel
            header={{ icon: { icon: 'check-circle', color: 'green', size: '5x' } }}
            responsiveness={{ compressed: false }}
          >
            {{
              title: t('geodataSetup.finish.done', { ns: 'cloudProcessing' }),
              info: t('geodataSetup.finish.doneInfo', { ns: 'cloudProcessing' }),
            }}
          </IconPanel>
        </CenteredContainer>
      </Component>
    );
  }

  return (
    <Component>
      <h4>{t('geodataSetup.finish.title', { ns: 'cloudProcessing' })}</h4>
      <Container>
        <StepsContainer>
          {/* Step 1: Creating cloud process */}
          <StepStatusRow
            status={createStepStatus}
            text={t('geodataSetup.finish.creatingCloudProcess', { ns: 'cloudProcessing' })}
            visible={true}
          />

          {/* Step 2: Uploading boundary polygons */}
          <StepStatusRow
            progress={boundaryProgress}
            showProgress={boundaryUploadQueue.totalCount > 0}
            status={boundaryStepStatus}
            text={t('geodataSetup.finish.uploadingBoundaryPolygonsWithCount', {
              currentIndex: boundaryUploadQueue.currentIndex,
              totalCount: boundaryUploadQueue.totalCount,
              ns: 'cloudProcessing',
            })}
            visible={
              !context.useInteractiveSetup &&
              createStepStatus === 'success' &&
              context.boundaryFiles.length > 0
            }
          />

          {/* Step 3: Uploading images */}
          <StepStatusRow
            progress={imageProgress}
            showProgress={imageUploadQueue.totalCount > 0}
            status={imageStepStatus}
            text={t('geodataSetup.finish.uploadingImagesWithCount', {
              completedCount: imageUploadQueue.completedCount,
              totalCount: imageUploadQueue.totalCount,
              ns: 'cloudProcessing',
            })}
            visible={
              !context.useInteractiveSetup &&
              createStepStatus === 'success' &&
              boundaryStepStatus === 'success'
            }
          />

          {/* Step 4: Controlling images */}
          <StepStatusRow
            status={controlImagesStepStatus}
            text={t('geodataSetup.finish.controllingImagesWithCount', {
              enabledCount: controlImagesEnabledCount,
              completedCount: imageUploadQueue.completedCount,
              ns: 'cloudProcessing',
            })}
            visible={
              !context.useInteractiveSetup &&
              (imageStepStatus === 'success' || controlImagesStepStatus !== 'pending') &&
              imageUploadQueue.completedCount > 0
            }
          />

          {/* Step 5: Starting cloud process */}
          <StepStatusRow
            status={startProcessStepStatus}
            text={t('geodataSetup.finish.startingCloudProcess', { ns: 'cloudProcessing' })}
            visible={!context.useInteractiveSetup && controlImagesStepStatus === 'success'}
          />
        </StepsContainer>
        {createGeodataErrorList}
        {boundaryUploadErrorList}
        {imageUploadErrorList}
        {controlImagesErrorList}
        {startProcessErrorList}
      </Container>
    </Component>
  );
};

type StepStatusRowProps = {
  status: 'pending' | 'loading' | 'success' | 'error';
  text: string;
  progress?: number;
  showProgress?: boolean;
  visible: boolean;
};

const StepStatusRow = ({ status, text, progress, showProgress, visible }: StepStatusRowProps) => {
  const getIcon = () => {
    if (status === 'pending' || status === 'loading') {
      return <Icon icon="spinner" spin={true} />;
    }
    if (status === 'success') {
      return <Icon color="green" icon="check-circle" />;
    }
    if (status === 'error') {
      return <Icon color="red" icon="times-circle" />;
    }
    return null;
  };

  if (!visible) {
    return null;
  }

  return (
    <StepRow>
      <StepContent>
        {getIcon()}
        <StepText>{text}</StepText>
      </StepContent>
      {showProgress && progress !== undefined && status === 'loading' && (
        <ProgressBarContainer>
          <ProgressBar max={100} min={0} value={progress} />
        </ProgressBarContainer>
      )}
    </StepRow>
  );
};

const Component = styled(Stack)`
  height: 100%;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: 1rem;
  gap: 1rem;
`;

const CenteredContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 1rem;
`;

const StepsContainer = styled(Stack)`
  flex-direction: column;
  gap: 1.5rem;
  flex: 1;
`;

const StepRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
`;

const StepContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StepText = styled.span`
  font-size: 1em;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  margin-top: 0.5rem;
`;
